import { useMemo } from 'react';

import type { Capabilities } from '@rtkwlf/rendall';
import { useSuspenseQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useLegacyCapabilitiesOptions } from '@apis/rendall/queries/use-capabilities';
import { useSubscriptionsQueryOptions } from '@apis/rendall/queries/use-subscriptions';
import { EVENT_LOG_SEARCH_ROUTE } from 'ae-constants/routes.constants';

import { DatePickerShortcuts } from '../use-date-picker-shortcuts/use-date-picker-shortcuts';

const DaysInWeek = 7;

const getDefaultDateRangeCallback =
  (maxRange: number) => (): [start: Dayjs, end: Dayjs] => {
    const today = dayjs();
    const defaultRangeDays = maxRange < DaysInWeek ? maxRange : DaysInWeek;
    const stateDate = today.subtract(defaultRangeDays, 'days');
    return [stateDate, today];
  };

interface DataExplorerBaseConfiguration {
  title: string;
  eventLogResultsTranslationKey: string;
  menuItem: {
    text: string;
    to: string;
    legacyCapabilities?: Partial<Capabilities>;
  };
}

interface DataExplorerConfiguration extends DataExplorerBaseConfiguration {
  customAlertsLimit: number;
  datePicker: {
    maxRange: number;
    menuOptions: DatePickerShortcuts[];
    getDefaultDateRange: () => [start: Dayjs, end: Dayjs];
  };
}

function useDataExplorerConfigurations() {
  const { t } = useTranslation();

  return useMemo<{
    full: DataExplorerBaseConfiguration;
    lite: DataExplorerBaseConfiguration;
  }>(
    () => ({
      full: {
        title: t('navMenu:dataExplorer'),
        eventLogResultsTranslationKey:
          'eventLogSearch:showingAnalyzedResults.full',
        menuItem: {
          legacyCapabilities: { canAccessAnalyzedData: true },
          text: t('navMenu:dataExplorer'),
          to: EVENT_LOG_SEARCH_ROUTE,
        },
      },
      lite: {
        title: t('navMenu:dataExplorerLite'),
        eventLogResultsTranslationKey:
          'eventLogSearch:showingAnalyzedResults.lite',
        menuItem: {
          legacyCapabilities: { canAccessAnalyzedDataLite: true },
          text: t('navMenu:dataExplorerLite'),
          to: EVENT_LOG_SEARCH_ROUTE,
        },
      },
    }),
    [t],
  );
}

export const useDataExplorerSuspenseConfigurationV2 =
  (): DataExplorerConfiguration => {
    const {
      data: { canAccessAnalyzedData },
    } = useSuspenseQuery(useLegacyCapabilitiesOptions());
    const {
      data: { dataExplorationDays, customAlertsLimit },
    } = useSuspenseQuery(useSubscriptionsQueryOptions());
    const { full: fullConfiguration, lite: liteConfiguration } =
      useDataExplorerConfigurations();

    const dataExplorerConfiguration = useMemo<DataExplorerConfiguration>(() => {
      const dataExplorerBaseConfig = canAccessAnalyzedData
        ? fullConfiguration
        : liteConfiguration;

      const datePicker = {
        maxRange: dataExplorationDays,
        menuOptions: [
          DatePickerShortcuts.PastHour,
          DatePickerShortcuts.Past24,
          DatePickerShortcuts.Past72,
        ],
        getDefaultDateRange: getDefaultDateRangeCallback(dataExplorationDays),
      };
      if (dataExplorationDays >= DaysInWeek) {
        datePicker.menuOptions.push(DatePickerShortcuts.PastWeek);
      }
      return {
        ...dataExplorerBaseConfig,
        customAlertsLimit,
        datePicker,
      };
    }, [
      fullConfiguration,
      liteConfiguration,
      canAccessAnalyzedData,
      dataExplorationDays,
      customAlertsLimit,
    ]);

    return dataExplorerConfiguration;
  };
